import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url, 
  type = 'website',
  structuredData,
  noIndex = false,
  canonicalUrl,
  breadcrumbs = null,
  isService = false,
  serviceData = null
}) => {
  const defaultTitle = 'Parla Branding | Hjemmesider der er en Investering, ikke en Udgift';
  const defaultDescription = 'Er jeres nuværende hjemmeside et aktiv, der skaber kunder? Vi er specialister i at bygge professionelle, skræddersyede hjemmesider, der leverer målbare forretningsresultater.';
  const defaultKeywords = 'hjemmeside investering, professionel hjemmeside, skræddersyede hjemmesider, hjemmeside der konverterer, webdesign til forretning, hjemmeside ROI, målbare forretningsresultater';
  const defaultImage = '/og-image.jpg';
  const baseUrl = 'https://www.parlabranding.dk';

  const seoTitle = title || defaultTitle;
  const seoDescription = description || defaultDescription;
  const seoKeywords = keywords || defaultKeywords;
  const seoImage = image || defaultImage;
  const seoUrl = url || baseUrl;
  const fullImageUrl = seoImage.startsWith('http') ? seoImage : `${baseUrl}${seoImage}`;
  const canonical = canonicalUrl || seoUrl;

  // Enhanced Organization Schema for Google
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Parla Branding",
    "url": baseUrl,
    "logo": `${baseUrl}/logo.jpg`,
    "description": "Professionelle hjemmesider og webdesign for danske virksomheder",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "DK",
      "addressRegion": "Danmark",
      "addressLocality": "Holbæk"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+45-36-17-00-00",
      "contactType": "customer service",
      "areaServed": "DK",
      "availableLanguage": "Danish"
    },
    "sameAs": [
      "https://www.facebook.com/ParlaBranding/",
      "https://dk.linkedin.com/company/parlabranding",
      "https://www.instagram.com/parlabranding.dk"
    ],
    "priceRange": "10000-75000 DKK",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "13",
      "bestRating": "5",
      "worstRating": "1"
    },
    "offers": {
      "@type": "Offer",
      "description": "Professionelle hjemmesider og webdesign tjenester for danske virksomheder. SEO-optimering tilbydes som tillæg for øget synlighed.",
      "priceRange": "10000-75000 DKK",
      "priceCurrency": "DKK"
    },
    "knowsAbout": [
      "Webdesign",
      "Hjemmeside udvikling", 
      "SEO optimering",
      "Responsive design",
      "Website udvikling"
    ],
    "serviceArea": {
      "@type": "Country",
      "name": "Danmark"
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content="Parla Branding" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="Danish" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Robots */}
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'} />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      
      {/* Open Graph */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Parla Branding" />
      <meta property="og:locale" content="da_DK" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@parlabranding" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={fullImageUrl} />
      
      {/* Additional SEO Tags */}
      <meta name="theme-color" content="#000000" />
      <meta name="msapplication-TileColor" content="#000000" />
      <meta name="application-name" content="Parla Branding" />
      
      {/* Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      
      {/* Service Schema for service pages */}
      {isService && serviceData && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": serviceData.name,
            "description": serviceData.description,
            "provider": {
              "@type": "Organization",
              "name": "Parla Branding"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Danmark"
            },
            "offers": {
              "@type": "Offer",
              "price": serviceData.price || "10000",
              "priceCurrency": "DKK"
            }
          })}
        </script>
      )}

      {/* Breadcrumbs Schema */}
      {breadcrumbs && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbs.map((item, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": item.name,
              "item": `https://www.parlabranding.dk${item.url}`
            }))
          })}
        </script>
      )}
      
      {/* Additional Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO; 